import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://bi.salonsoftware.my';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('ccs_period');
      localStorage.removeItem('ccs_from');
      localStorage.removeItem('ccs_to');
      localStorage.removeItem('ccs_gran');
      localStorage.removeItem('ccs_branch');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================================
// AUTH
// ============================================================
export const login = (username, password) =>
  api.post('/api/auth/login', { username, password });

// ============================================================
// SALES
// ============================================================
export const getSalesOverview      = (params) => api.get('/api/sales/overview',        { params });
export const getSalesTrend         = (params) => api.get('/api/sales/trend',           { params });
export const getSalesDrill         = (params) => api.get('/api/sales/drill',           { params });
export const getSalesFilterOptions = (params) => api.get('/api/sales/filter-options', { params });

// ============================================================
// VISUAL PREFERENCES
// ============================================================
export const getVisualPrefs = ()     => api.get('/api/prefs/visuals');
export const setVisualPref  = (data) => api.post('/api/prefs/visuals', data);

// ============================================================
// STAFF
// ============================================================
export const getStaffPerformance = (params) => api.get('/api/staff/performance', { params });
export const getStaffTrend       = (params) => api.get('/api/staff/trend',       { params });
export const getStaffAttendance  = (params) => api.get('/api/staff/attendance',  { params });

// ============================================================
// PAYMENTS
// ============================================================
export const getPaymentAnalysis      = (params) => api.get('/api/payment/analysis',        { params });
export const getPaymentFilterOptions = (params) => api.get('/api/payment/filter-options',  { params });

// ============================================================
// CUSTOMERS
// ============================================================
export const getCustomerInsights       = (params) => api.get('/api/customers/insights',        { params });
export const getCustomerFilterOptions  = (params) => api.get('/api/customers/filter-options',  { params });
export const getCustomerBirthdayMonths = (params = {}) => api.get('/api/customers/birthday-months', { params });
export const getCustomerBirthdayDays   = (month, params = {}) => api.get('/api/customers/birthday-days', { params: { month, ...params } });
export const listCustomers       = (params) => api.get('/api/customers/list',     { params });
export const getCustomerProfile  = (memberCode, params = {}) =>
  api.get('/api/customers/profile', { params: { member_code: memberCode, ...params } });

// ============================================================
// BOOKINGS
// ============================================================
export const getBookingSummary       = (params) => api.get('/api/booking/summary',        { params });
export const getBookingTrend         = (params) => api.get('/api/booking/trend',          { params });
export const getBookingFilterOptions = (params) => api.get('/api/booking/filter-options', { params });

// ============================================================
// AUTH - ADDITIONAL
// ============================================================
export const changePassword = (data) => api.post('/api/auth/change-password', data);
export const verifySession  = ()     => api.get('/api/auth/verify-session');

// ============================================================
// SALES - ADDITIONAL TRENDS
// ============================================================
export const getSalesAvgValueTrend    = (params) => api.get('/api/sales/avg-value-trend',   { params });
export const getSalesPeriodComparison = (params) => api.get('/api/sales/period-comparison', { params });

// ============================================================
// STAFF - ADDITIONAL TRENDS
// ============================================================
export const getStaffServicesTrend   = (params) => api.get('/api/staff/services-trend',    { params });
export const getStaffAvgRevenueTrend = (params) => api.get('/api/staff/avg-revenue-trend', { params });

// ============================================================
// REPORTS - CORE
// ============================================================
export const getReportFilterOptions = (tenantId = null) =>
  api.get('/api/reports/filter-options', { params: tenantId ? { sa_tenant: tenantId } : {} });
export const generateReport         = (params) => api.get('/api/reports/generate', { params });

// ============================================================
// REPORTS - SAVED (boss saves, names, and manages reports)
// ============================================================
export const getSavedReports   = (tenantId = null) =>
  api.get('/api/reports/saved', { params: tenantId ? { sa_tenant: tenantId } : {} });
export const saveReport        = (data)       => api.post('/api/reports/save', data);
export const deleteSavedReport = (id, tenantId = null) =>
  api.delete(`/api/reports/saved/${id}`, { params: tenantId ? { sa_tenant: tenantId } : {} });

// ============================================================
// REPORTS - SHARING (boss shares saved reports with managers)
// ============================================================

// Get the list of managers in this tenant (for the share dropdown)
export const getSharableUsers = (tenantId = null) =>
  api.get('/api/reports/sharable-users', { params: tenantId ? { sa_tenant: tenantId } : {} });

// Update who can see a specific report
// sharedUserIds: array of user_id integers e.g. [3, 7, 12]
export const shareReport = (reportId, sharedUserIds, tenantId = null) =>
  api.post(`/api/reports/saved/${reportId}/share`, {
    shared_with_users: sharedUserIds,
    ...(tenantId ? { sa_tenant: tenantId } : {}),
  });

// ============================================================
// GLOBAL REPORTS (superadmin creates once, assigns to many tenants)
// Each tenant's boss sees it rendered with their own data.
// ============================================================
export const getGlobalReports   = ()                      => api.get('/api/reports/global');
export const saveGlobalReport   = (data)                  => api.post('/api/reports/global/save', data);
export const deleteGlobalReport = (id)                    => api.delete(`/api/reports/global/${id}`);
export const assignGlobalReport = (id, tenantIds)         => api.post(`/api/reports/global/${id}/assign`, { tenant_ids: tenantIds });
export const shareGlobalReport  = (id, sharedUserIds)     => api.post(`/api/reports/global/${id}/share`, { shared_with_users: sharedUserIds });

// ============================================================
// REPORTS - SQL QUERY TOOL (boss only)
// Runs a raw SELECT query, returns { columns, data, row_count, capped }
// capped=true means results were limited to 1000 rows
// ============================================================
export const runSqlQuery = (sql, tenantId = null) =>
  api.post('/api/reports/sql-query', { sql, ...(tenantId ? { sa_tenant: tenantId } : {}) });

// ── Saved SQL Queries ────────────────────────────────────────
 
// Boss: save a new query
export const saveSqlQuery = (data) =>
  api.post('/api/reports/sql-queries/save', data);
// data = { query_name: string, sql_query: string, sa_tenant?: string }

// Boss: get own saved queries. Manager: get queries shared with them.
export const getSavedSqlQueries = (tenantId = null) =>
  api.get('/api/reports/sql-queries/saved', { params: tenantId ? { sa_tenant: tenantId } : {} });
 
// Boss: delete a saved query
export const deleteSavedSqlQuery = (id, tenantId = null) =>
  api.delete(`/api/reports/sql-queries/saved/${id}`, { params: tenantId ? { sa_tenant: tenantId } : {} });
 
// Boss: update who can run this query
export const shareSqlQuery = (id, sharedUserIds, tenantId = null) =>
  api.post(`/api/reports/sql-queries/saved/${id}/share`, {
    shared_with_users: sharedUserIds,
    ...(tenantId ? { sa_tenant: tenantId } : {}),
  });
 
// Boss or Manager: run a saved query by id
// Boss runs own query, Manager runs a query shared with them
export const runSavedSqlQuery = (id, tenantId = null) =>
  api.post(`/api/reports/sql-queries/saved/${id}/run`, tenantId ? { sa_tenant: tenantId } : {});


// ============================================================
// TEAM MANAGEMENT (Boss)
// ============================================================
export const getTeamUsers      = (params)     => api.get('/api/team/users',                  { params });
export const createTeamUser    = (data)        => api.post('/api/team/users',                 data);
export const updateTeamUser    = (id, data)    => api.put(`/api/team/users/${id}`,            data);
export const archiveTeamUser   = (id)          => api.post(`/api/team/users/${id}/archive`);
export const unarchiveTeamUser = (id)          => api.post(`/api/team/users/${id}/unarchive`);
export const getTeamBranches   = (tenantId = null) => api.get('/api/team/branches', { params: tenantId ? { sa_tenant: tenantId } : {} });

// ============================================================
// BRANCH COMPARISON
// ============================================================
export const getBranches       = ()       => api.get('/api/branches');
export const getBranchSummary  = (params) => api.get('/api/branch/summary', { params });
export const getBranchTrend    = (params) => api.get('/api/branch/trend',   { params });

// ============================================================
// SUPERADMIN
// ============================================================
export const getSuperadminTenants        = ()                => api.get('/api/superadmin/tenants');
export const createSuperadminTenant      = (data)            => api.post('/api/superadmin/tenants', data);
export const updateSuperadminTenant      = (tenantId, data)  => api.put(`/api/superadmin/tenants/${tenantId}`, data);
export const toggleSuperadminTenant      = (tenantId)        => api.post(`/api/superadmin/tenants/${tenantId}/toggle`);
export const getSuperadminUsers          = (params)          => api.get('/api/superadmin/users', { params });
export const createSuperadminUser        = (data)            => api.post('/api/superadmin/users', data);
export const toggleSuperadminUser        = (userId)          => api.post(`/api/superadmin/users/${userId}/toggle`);
export const updateSuperadminUser        = (userId, data)    => api.put(`/api/superadmin/users/${userId}`, data);
export const archiveSuperadminUser       = (userId)          => api.post(`/api/superadmin/users/${userId}/archive`);
export const unarchiveSuperadminUser     = (userId)          => api.post(`/api/superadmin/users/${userId}/unarchive`);
export const impersonateSuperadminUser   = (userId)          => api.post(`/api/superadmin/impersonate/${userId}`);
export const resetSuperadminUserPassword = (userId, data)    => api.post(`/api/superadmin/users/${userId}/reset-password`, data);

export default api;